import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { fr, ar, enUS } from 'date-fns/locale';
import { fetchNewsById, addBookmark, removeBookmark } from '../../redux/actions/newsAction';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShareIcon from '@mui/icons-material/Share';
import {
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Snackbar,
    Box,
    Button,
    ButtonGroup,
    useTheme,
    Typography,
    alpha
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { NewsDetailSkeleton } from './components/NewsSkeleton';


const NewsDetailScreen = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { newsId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

    // Get state from Redux
    const {
        newsItem,
        loading,
        errors
    } = useSelector(state => state.legalNews);

    const isRtl = newsItem?.language === 'ar';
    const contentDir = isRtl ? 'rtl' : 'ltr';
    const availableTranslations = newsItem?.available_translations || {};
    const hasTranslations = Object.keys(availableTranslations).length > 1;

    // Fetch the news item when the component mounts or newsId changes
    useEffect(() => {
        dispatch(fetchNewsById(newsId));
    }, [dispatch, newsId]);

    const handleLanguageSwitch = (lang) => {
        const translationId = availableTranslations[lang];
        if (translationId && translationId !== newsItem.id) {
            navigate(`/legal-news/${translationId}`, { replace: true });
        }
    };

    // Format date based on article language
    const dateLocale = newsItem?.language === 'ar' ? ar : newsItem?.language === 'en' ? enUS : fr;
    const formattedDate = newsItem?.published_at ?
        format(new Date(newsItem.published_at), 'dd MMMM yyyy', { locale: dateLocale }) :
        '';

    // Calculate reading time based on content length
    const calculateReadingTime = (content) => {
        if (!content) return '< 1 min';
        
        // Average reading speed: 200 words per minute
        const wordCount = content.split(/\s+/).length;
        const minutes = Math.ceil(wordCount / 200);
        
        return minutes <= 1 ? '1 min' : `${minutes} mins`;
    };

    // Handle bookmark toggle
    const handleBookmarkToggle = () => {
        if (!isAuthenticated) {
            // Redirect to login if not authenticated
            navigate('/login?redirect=/legal-news/' + newsId);
            return;
        }

        if (newsItem.is_bookmarked) {
            dispatch(removeBookmark(newsItem.id));
            setSnackbarMessage(t('news_hub.bookmarks.remove'));
        } else {
            dispatch(addBookmark(newsItem.id));
            setSnackbarMessage(t('news_hub.bookmarks.add'));
        }
        setOpenSnackbar(true);
    };

    // Handle share functionality
    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: newsItem.title,
                text: newsItem.summary,
                url: window.location.href,
            })
            .then(() => console.log('Shared successfully'))
            .catch((error) => console.log('Error sharing:', error));
        } else {
            // Fallback for browsers that don't support navigator.share
            navigator.clipboard.writeText(window.location.href);
            setSnackbarMessage(t('news_hub.link_copied'));
            setOpenSnackbar(true);
        }
    };

    // Markdown renderer function
    const renderMarkdown = (content) => {
        if (!content) return '';
        
        // Split content by paragraphs
        const paragraphs = content.split('\n');
        
        return paragraphs.map((paragraph, index) => {
            // Skip empty paragraphs
            if (!paragraph.trim()) return null;
            
            // Handle heading elements (h1-h6)
            if (paragraph.startsWith('# ')) {
                return <h1 key={index} className="text-3xl font-bold my-4">{paragraph.substring(2)}</h1>;
            } else if (paragraph.startsWith('## ')) {
                return <h2 key={index} className="text-2xl font-bold my-3">{paragraph.substring(3)}</h2>;
            } else if (paragraph.startsWith('### ')) {
                return <h3 key={index} className="text-xl font-bold my-3">{paragraph.substring(4)}</h3>;
            } else if (paragraph.startsWith('#### ')) {
                return <h4 key={index} className="text-lg font-bold my-2">{paragraph.substring(5)}</h4>;
            } else if (paragraph.startsWith('##### ')) {
                return <h5 key={index} className="text-base font-bold my-2">{paragraph.substring(6)}</h5>;
            } else if (paragraph.startsWith('###### ')) {
                return <h6 key={index} className="text-sm font-bold my-2">{paragraph.substring(7)}</h6>;
            }
            
            // Handle lists
            if (paragraph.split('\n').every(line => line.trim().startsWith('- ') || line.trim().startsWith('* '))) {
                const listItems = paragraph.split('\n').map((line, i) => {
                    const itemContent = line.trim().substring(2);
                    return <li key={i} className="ml-6 mb-1">{processInlineMarkdown(itemContent)}</li>;
                });
                return <ul key={index} className="list-disc my-4">{listItems}</ul>;
            }
            
            if (paragraph.split('\n').every(line => {
                const match = line.trim().match(/^(\d+)\.\s(.+)/);
                return match !== null;
            })) {
                const listItems = paragraph.split('\n').map((line, i) => {
                    const match = line.trim().match(/^(\d+)\.\s(.+)/);
                    return <li key={i} className="ml-6 mb-1">{processInlineMarkdown(match[2])}</li>;
                });
                return <ol key={index} className="list-decimal my-4">{listItems}</ol>;
            }

            // Handle blockquotes
            if (paragraph.startsWith('> ')) {
                const quoteContent = paragraph.substring(2).split('\n> ').join('\n');
                return (
                    <blockquote
                        key={index}
                        style={{
                            ...(isRtl
                                ? { borderRight: `4px solid ${theme.palette.primary.main}`, paddingRight: '16px' }
                                : { borderLeft: `4px solid ${theme.palette.primary.main}`, paddingLeft: '16px' }
                            ),
                            fontStyle: 'italic',
                            margin: '16px 0',
                            color: theme.palette.text.secondary
                        }}
                    >
                        {processInlineMarkdown(quoteContent)}
                    </blockquote>
                );
            }

            // Handle code blocks
            if (paragraph.startsWith('```') && paragraph.endsWith('```')) {
                const codeContent = paragraph.substring(paragraph.indexOf('\n') + 1, paragraph.lastIndexOf('```'));
                return (
                    <pre
                        key={index}
                        style={{
                            backgroundColor: theme.palette.action.hover,
                            padding: '16px',
                            borderRadius: '6px',
                            overflowX: 'auto',
                            margin: '16px 0',
                            color: theme.palette.text.primary
                        }}
                    >
                        <code>{codeContent}</code>
                    </pre>
                );
            }
            
            // Default to regular paragraph with inline markdown processing
            return <p key={index} className="my-4">{processInlineMarkdown(paragraph)}</p>;
        });
    };

    // Process inline markdown elements (bold, italic, links, inline code)
    const processInlineMarkdown = (text) => {
        if (!text) return '';
        
        // Break the text into parts to handle inline elements
        let parts = [];
        let currentText = '';
        let inBold = false;
        let inItalic = false;
        let inCode = false;
        
        const processPart = (part) => {
            // Process links - [text](url)
            const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
            let linkResult = part;
            let match;
            
            while ((match = linkRegex.exec(part)) !== null) {
                const linkText = match[1];
                const url = match[2];
                const link = (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                            e.currentTarget.style.color = theme.palette.primary.dark;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                            e.currentTarget.style.color = theme.palette.primary.main;
                        }}
                    >
                        {linkText}
                    </a>
                );
                
                // Replace the part before the link
                const beforeLink = part.substring(0, match.index);
                // Find the part after the link
                const afterLink = part.substring(match.index + match[0].length);
                
                // Update the current result
                return beforeLink && <>{beforeLink} {link}</> || link;
            }
            
            return linkResult;
        };
        
        // Handle all inline markdown elements
        for (let i = 0; i < text.length; i++) {
            if (text[i] === '`' && (i === 0 || text[i-1] !== '\\')) {
                parts.push(inCode ? (
                    <code
                        key={parts.length}
                        style={{
                            backgroundColor: theme.palette.action.hover,
                            padding: '2px 4px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: 'monospace',
                            color: theme.palette.text.primary
                        }}
                    >
                        {currentText}
                    </code>
                ) : currentText);
                currentText = '';
                inCode = !inCode;
                continue;
            }
            
            if (text.substring(i, i+2) === '**' && (i === 0 || text[i-1] !== '\\')) {
                parts.push(inBold ? <strong key={parts.length}>{currentText}</strong> : processPart(currentText));
                currentText = '';
                inBold = !inBold;
                i++; // Skip the next character
                continue;
            }
            
            if (text[i] === '*' && (i === 0 || text[i-1] !== '\\') && 
                (i+1 >= text.length || text[i+1] !== '*')) {
                parts.push(inItalic ? <em key={parts.length}>{currentText}</em> : processPart(currentText));
                currentText = '';
                inItalic = !inItalic;
                continue;
            }
            
            currentText += text[i];
        }
        
        // Add any remaining text
        if (currentText) {
            parts.push(inBold ? <strong key={parts.length}>{currentText}</strong> :
                      inItalic ? <em key={parts.length}>{currentText}</em> :
                      inCode ? (
                          <code
                              key={parts.length}
                              style={{
                                  backgroundColor: theme.palette.action.hover,
                                  padding: '2px 4px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontFamily: 'monospace',
                                  color: theme.palette.text.primary
                              }}
                          >
                              {currentText}
                          </code>
                      ) :
                      processPart(currentText));
        }
        
        return <>{parts}</>;
    };

    // Display skeleton while loading
    if (loading.newsItem) {
        return <NewsDetailSkeleton />;
    }

    if (errors.newsItem) {
        return (
            <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
                <Alert severity="error">{errors.newsItem}</Alert>
                <Box sx={{ mt: 2 }}>
                    <Box
                        onClick={() => navigate('/legal-news')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                color: theme.palette.primary.dark
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{ mr: 1 }} />
                        {t('news_hub.back_to_news')}
                    </Box>
                </Box>
            </Box>
        );
    }

    if (!newsItem) {
        return (
            <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
                <Alert severity="warning">Article non trouvé</Alert>
                <Box sx={{ mt: 2 }}>
                    <Box
                        onClick={() => navigate('/legal-news')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.primary.main,
                            cursor: 'pointer',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                color: theme.palette.primary.dark
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{ mr: 1 }} />
                        {t('news_hub.back_to_news')}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', p: 2 }}>
            {/* Back button */}
            <Box sx={{ mb: 3 }}>
                <Box
                    onClick={() => navigate('/legal-news')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                            color: theme.palette.primary.dark
                        }
                    }}
                >
                    <ArrowBackIcon sx={{ mr: 1 }} />
                    {t('news_hub.back_to_news')}
                </Box>
            </Box>

            <Box
                component="article"
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '12px',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.05)',
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`
                }}
            >
                {/* Article header */}
                <Box component="header">
                    {/* Cover image */}
                    <Box
                        sx={{
                            position: 'relative',
                            height: '256px',
                            backgroundColor: theme.palette.action.hover,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {newsItem.image_url ? (
                            <img
                                src={newsItem.image_url}
                                alt={newsItem.title}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <ArticleIcon
                                sx={{
                                    width: '96px',
                                    height: '96px',
                                    color: theme.palette.text.tertiary
                                }}
                            />
                        )}

                        {/* Featured badge */}
                        {newsItem.is_featured && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    left: 16,
                                    backgroundColor: theme.palette.warning.main,
                                    color: '#000000',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('news_hub.featured')}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ p: 3 }} dir={contentDir}>
                        {/* Language switch + Categories row */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {newsItem.categories && newsItem.categories.map((category, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'inline-block',
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: 500
                                        }}
                                    >
                                        {t(`news_category.${category}`)}
                                    </Box>
                                ))}
                            </Box>

                            {/* Language switch */}
                            {hasTranslations && (
                                <ButtonGroup size="small" sx={{ flexShrink: 0 }}>
                                    {Object.keys(availableTranslations).map((lang) => (
                                        <Button
                                            key={lang}
                                            onClick={() => handleLanguageSwitch(lang)}
                                            variant={newsItem.language === lang ? 'contained' : 'outlined'}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: newsItem.language === lang ? 600 : 400,
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                minWidth: 0,
                                            }}
                                        >
                                            {lang === 'ar' ? 'العربية' : lang === 'fr' ? 'Français' : 'English'}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            )}
                        </Box>

                        {/* Title */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography
                                component="h1"
                                sx={{
                                    fontSize: '32px',
                                    fontWeight: 'bold',
                                    fontFamily: isRtl ? undefined : theme.typography.h1?.fontFamily,
                                    color: theme.palette.text.primary,
                                    mb: 2,
                                    textAlign: isRtl ? 'right' : 'left',
                                    flex: 1,
                                }}
                            >
                                {newsItem.title}
                            </Typography>

                            {/* Action buttons */}
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                {/* Bookmark button */}
                                <Tooltip title={newsItem.is_bookmarked ? t('news_hub.bookmarks.remove') : t('news_hub.bookmarks.add')}>
                                    <IconButton
                                        onClick={handleBookmarkToggle}
                                        disabled={loading.addBookmark || loading.removeBookmark}
                                        sx={{
                                            color: newsItem.is_bookmarked
                                                ? theme.palette.warning.main
                                                : theme.palette.text.secondary,
                                            '&:hover': {
                                                color: newsItem.is_bookmarked
                                                    ? theme.palette.warning.light
                                                    : theme.palette.text.primary,
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        {loading.addBookmark || loading.removeBookmark ? (
                                            <CircularProgress size={24} />
                                        ) : newsItem.is_bookmarked ? (
                                            <BookmarkIcon />
                                        ) : (
                                            <BookmarkBorderIcon />
                                        )}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t('news_hub.share')}>
                                    <IconButton
                                        onClick={handleShare}
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                color: theme.palette.text.primary,
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <ShareIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Meta information */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', color: theme.palette.text.secondary, mb: 3, gap: 1 }}>
                            <Box sx={{ fontWeight: 500 }}>
                                {newsItem.source_name || 'Lawis News'}
                            </Box>
                            <Box>•</Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccessTimeIcon sx={{ width: '16px', height: '16px', ...(isRtl ? { ml: 0.5 } : { mr: 0.5 }) }} />
                                <span>{calculateReadingTime(newsItem.content)}</span>
                            </Box>
                            <Box>•</Box>
                            <Box>{formattedDate}</Box>
                        </Box>

                        {/* Summary */}
                        <Box
                            sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                p: 2,
                                borderRadius: '8px',
                                mb: 3,
                                ...(isRtl
                                    ? { borderRight: `4px solid ${theme.palette.primary.main}` }
                                    : { borderLeft: `4px solid ${theme.palette.primary.main}` }
                                ),
                            }}
                        >
                            <Typography sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                {newsItem.summary}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Article content */}
                <Box sx={{ px: 3, pb: 3 }} dir={contentDir}>
                    <Box
                        sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.8,
                            '& h1': { fontSize: '28px', fontWeight: 700, my: 2, color: theme.palette.text.primary },
                            '& h2': { fontSize: '22px', fontWeight: 700, my: 1.5, color: theme.palette.text.primary },
                            '& h3': { fontSize: '20px', fontWeight: 700, my: 1.5, color: theme.palette.text.primary },
                            '& h4': { fontSize: '18px', fontWeight: 600, my: 1.5, color: theme.palette.text.primary },
                            '& h5': { fontSize: '16px', fontWeight: 600, my: 1.5, color: theme.palette.text.primary },
                            '& h6': { fontSize: '14px', fontWeight: 600, my: 1.5, color: theme.palette.text.primary },
                            '& ul, & ol': { my: 2, ...(isRtl ? { mr: 3 } : { ml: 3 }) },
                            '& li': { mb: 1 },
                            '& p': { my: 2 },
                        }}
                    >
                        {renderMarkdown(newsItem.content)}
                    </Box>
                </Box>

                {/* Article footer */}
                <Box
                    component="footer"
                    sx={{
                        px: 3,
                        pb: 3,
                        mt: 4,
                        pt: 3,
                        borderTop: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ fontSize: '14px', color: theme.palette.text.secondary }}>
                            {newsItem.author && (
                                <Box sx={{ mb: 1 }}>Auteur: {newsItem.author}</Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Snackbar for notifications */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                message={snackbarMessage}
            />
        </Box>
    );
};

export default NewsDetailScreen;